import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getFirstTouchPayload } from '@/lib/firstTouch';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** Increments on every real SIGNED_IN event. Initial session restores emit INITIAL_SESSION, not SIGNED_IN. */
  signInTick: number;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signInTick, setSignInTick] = useState(0);
  const signingOutRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // Keep the authenticated user sticky across route changes and short session
    // refresh gaps. Only clear it when the backend confirms SIGNED_OUT or the
    // user explicitly logs out.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        if (event === 'SIGNED_OUT' || signingOutRef.current) {
          setSession(null);
          setUser(null);
          signingOutRef.current = false;
          setLoading(false);
          return;
        }

        if (event === 'SIGNED_IN') {
          // In supabase-js v2, SIGNED_IN only fires on real sign-ins. Session
          // restores after page load emit INITIAL_SESSION, so they do not trigger this.
          setSignInTick(t => t + 1);
        }

        if (session?.user) {
          setSession(session);
          setUser(session.user);
        }
      }
    );

    // INITIAL load (controls loading state)
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const username = displayName.trim();
    // First-Touch-Attribution (nur mit erteilter Einwilligung befüllt) wird als
    // Metadatum mitgegeben und serverseitig im Registrierungs-Trigger
    // persistiert. Fehler dürfen die Registrierung nie verhindern.
    let attribution: Record<string, unknown> = {};
    try {
      attribution = getFirstTouchPayload() as unknown as Record<string, unknown>;
    } catch {
      attribution = {};
    }
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          display_name: username,
          username,
          aimup_attr: attribution,
        },
      },
    });
    return { error: error as Error | null };
  };


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    signingOutRef.current = true;
    setSession(null);
    setUser(null);
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signInTick,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
